import React, { useEffect } from 'react'

import { useTheme } from 'next-themes'

import type { StoredTheme } from '@/constants/theme'
import { asStoredTheme } from '@/constants/theme'

interface UserThemeReporterProps {
  /** Called with the user's stored theme, and again whenever it changes. */
  onChange: (theme: StoredTheme | undefined) => void
}

/**
 * Republishes the user's stored theme to a listener outside the theme provider.
 *
 * A page that supports only some of the themes is pinned with the provider's
 * `forcedTheme` prop, which has to be decided *above* the provider — where the
 * user's pick is not readable. This sits just inside it and hands the pick back
 * up, so the decision can be made with both halves in hand and can be remade
 * when the user changes themes mid-page.
 *
 * Renders nothing, so republishing costs one render of the tree above and never
 * a hydration mismatch: `next-themes` seeds its state from `localStorage` on the
 * first client render, which the server had no way to match.
 */
export const UserThemeReporter: React.FC<UserThemeReporterProps> = ({
  onChange,
}) => {
  const { theme } = useTheme()

  useEffect(() => {
    onChange(asStoredTheme(theme))
  }, [theme, onChange])

  return null
}

export default UserThemeReporter
